import React, { useState } from 'react';

import Button from 'components/Button';
import ContactForm from 'components/ContactForm';
import LottieAnimation from 'components/LottieAnimation';
import dataEngAnimation from 'assets/animations/data_eng.json';

import {
  PageWrapper,
  HeroSection,
  HeroContainer,
  HeroContent,
  HeroTitle,
  HeroSubtitle,
  HeroAnimationWrapper,
  HeroGlowOrb,
  ServicesSection,
  SectionContainer,
  SectionTitle,
  SectionSubtitle,
  ServicesGrid,
  ServiceCard,
  ServiceIcon,
  ServiceTitle,
  ServiceDescription,
  TechStackSection,
  TechGrid,
  TechCard,
  TechName,
  TechCategory,
  ArchitectureSection,
  PipelineContainer,
  PipelineStep,
  PipelineStepNumber,
  PipelineStepTitle,
  PipelineStepDescription,
  BenefitsSection,
  BenefitsGrid,
  BenefitCard,
  BenefitStat,
  BenefitTitle,
  BenefitDescription,
  CTASection,
  CTACard,
  CTATitle,
  CTADescription,
  CTADeliverables,
  DeliverableItem,
  CTAButton,
  FAQSection,
  FAQContainer,
  FAQItem,
  FAQQuestion,
  FAQAnswer,
} from './DataEngineering.styles';

const DataEngineering = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const services = [
    {
      icon: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
          <path
            d="M12 16V4m0 0L8 8m4-4l4 4"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M3 15a4 4 0 0 0 4 4h10a4 4 0 0 0 2.8-6.8"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'Cloud Data Upload',
      description:
        'Migrate your data to the cloud securely. We move databases, spreadsheets, and files to platforms like Google Cloud, AWS, or Azure — so your data is accessible from anywhere.',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
          <path
            d="M4 4h16v16H4z"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M4 9h16" stroke="#06B6D4" strokeWidth="2" />
          <path
            d="M8 13h4M8 16h6"
            stroke="#06B6D4"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M15 13l2 2-2 2"
            stroke="#06B6D4"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      title: 'File Digitalization',
      description:
        'Turn paper documents, PDFs, and legacy files into structured, searchable digital data. We extract, classify, and store your information so nothing gets lost.',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
          <path
            d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path d="M9 3v14M15 7v14" stroke="#06B6D4" strokeWidth="2" />
        </svg>
      ),
      title: 'Cloud Drive Organization',
      description:
        'Structure your Google Drive, SharePoint, or S3 storage with clear folder hierarchies, naming conventions, and access controls — making files easy to find and manage.',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
          <circle cx="5" cy="12" r="2" stroke="#06B6D4" strokeWidth="2" />
          <circle cx="19" cy="7" r="2" stroke="#06B6D4" strokeWidth="2" />
          <circle cx="19" cy="17" r="2" stroke="#06B6D4" strokeWidth="2" />
          <path
            d="M7 11l10-3M7 13l10 3"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'Data Transformation Pipelines',
      description:
        'Build automated workflows that clean, combine, and reshape your data from multiple sources into consistent, analysis-ready formats — running on schedule without manual work.',
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none">
          <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="2"
            stroke="#06B6D4"
            strokeWidth="2"
          />
          <line
            x1="7"
            y1="17"
            x2="7"
            y2="12"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="12"
            y1="17"
            x2="12"
            y2="8"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="17"
            y1="17"
            x2="17"
            y2="10"
            stroke="#06B6D4"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
      title: 'Dashboards & Metrics',
      description:
        'Get real-time visibility into your business with interactive dashboards. We connect your data to tools like Power BI, Looker, or Tableau so your team can track KPIs at a glance.',
    },
  ];

  const techStack = [
    {
      name: 'Google Cloud',
      category: 'Cloud',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28">
          <path
            d="M14.5 9.5l1.5-2.6h3.2L15.3 13"
            stroke="#4285F4"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 17l-1.5-2.6 1.5-2.6 1.5 2.6"
            stroke="#FBBC05"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.5 9.5L8 6.9H4.8L8.7 13"
            stroke="#EA4335"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.7 13H4.8l3.9 6.7h3.2"
            stroke="#34A853"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.3 13h3.9l-3.9 6.7h-3.2"
            stroke="#4285F4"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: 'AWS',
      category: 'Cloud',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <path
            d="M4 14.5c2.5 2 6.5 3 10 2.5s5-2 6-3"
            stroke="#FF9900"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M18 14l2.5.5-1 2"
            stroke="#FF9900"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6.5 7l1.5 5.5L9.5 7"
            stroke="#fff"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11 7l1.5 5.5L14 7"
            stroke="#fff"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.5 7c1 0 1.8.8 1.5 2-.2 1-1 1.5-1.8 1.5h-.7l-.3 2"
            stroke="#fff"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      name: 'Azure',
      category: 'Cloud',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <path
            d="M9.5 3L4 19h4.5l1-3h5L13 19h5L14.5 3h-5z"
            fill="#0078D4"
            fillOpacity="0.9"
          />
          <path d="M10.5 12l2-6 2 6h-4z" fill="#50E6FF" fillOpacity="0.7" />
        </svg>
      ),
    },
    {
      name: 'Power BI',
      category: 'Dashboard',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <rect x="14" y="4" width="4.5" height="16" rx="1.5" fill="#F2C811" />
          <rect
            x="9"
            y="8"
            width="4.5"
            height="12"
            rx="1.5"
            fill="#F2C811"
            fillOpacity="0.7"
          />
          <rect
            x="4"
            y="12"
            width="4.5"
            height="8"
            rx="1.5"
            fill="#F2C811"
            fillOpacity="0.45"
          />
        </svg>
      ),
    },
    {
      name: 'Looker',
      category: 'Dashboard',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <circle cx="12" cy="11" r="5" stroke="#4285F4" strokeWidth="1.8" />
          <circle cx="12" cy="11" r="2" fill="#4285F4" />
          <path
            d="M15.5 15l4 4"
            stroke="#34A853"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      name: 'Tableau',
      category: 'Dashboard',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <line
            x1="12"
            y1="4"
            x2="12"
            y2="20"
            stroke="#E97627"
            strokeWidth="2.5"
          />
          <line
            x1="4"
            y1="12"
            x2="20"
            y2="12"
            stroke="#E97627"
            strokeWidth="2.5"
          />
          <line
            x1="6"
            y1="6"
            x2="6"
            y2="10"
            stroke="#5B879B"
            strokeWidth="1.8"
          />
          <line
            x1="4"
            y1="8"
            x2="8"
            y2="8"
            stroke="#5B879B"
            strokeWidth="1.8"
          />
          <line
            x1="18"
            y1="14"
            x2="18"
            y2="18"
            stroke="#5B879B"
            strokeWidth="1.8"
          />
          <line
            x1="16"
            y1="16"
            x2="20"
            y2="16"
            stroke="#5B879B"
            strokeWidth="1.8"
          />
          <line
            x1="17"
            y1="5"
            x2="17"
            y2="8"
            stroke="#E97627"
            strokeWidth="1.5"
          />
          <line
            x1="15.5"
            y1="6.5"
            x2="18.5"
            y2="6.5"
            stroke="#E97627"
            strokeWidth="1.5"
          />
        </svg>
      ),
    },
    {
      name: 'Python',
      category: 'Language',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <path
            d="M11.9 2C9.5 2 8 2.7 8 4.5V7h4v1H6.5C4.5 8 3 9.7 3 12s1.5 4 3.5 4H8v-2.5C8 11.5 9.5 10 12 10h4c1.5 0 2.5-1 2.5-2.5v-3C18.5 2.7 16.4 2 14 2h-2.1z"
            fill="#3776AB"
          />
          <path
            d="M12.1 22c2.4 0 3.9-.7 3.9-2.5V17h-4v-1h5.5c2 0 3.5-1.7 3.5-4s-1.5-4-3.5-4H16v2.5c0 2-1.5 3.5-4 3.5h-4c-1.5 0-2.5 1-2.5 2.5v3c0 1.8 2.1 2.5 4.5 2.5h2.1z"
            fill="#FFD43B"
          />
          <circle cx="10" cy="5.2" r="1" fill="#fff" />
          <circle cx="14" cy="18.8" r="1" fill="#fff" />
        </svg>
      ),
    },
    {
      name: 'SQL',
      category: 'Language',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <ellipse
            cx="12"
            cy="6.5"
            rx="7"
            ry="2.8"
            fill="#336791"
            fillOpacity="0.9"
          />
          <path
            d="M5 6.5v5c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8v-5"
            stroke="#336791"
            strokeWidth="1.5"
          />
          <path
            d="M5 11.5v5c0 1.55 3.13 2.8 7 2.8s7-1.25 7-2.8v-5"
            stroke="#336791"
            strokeWidth="1.5"
          />
          <ellipse
            cx="12"
            cy="6.5"
            rx="7"
            ry="2.8"
            stroke="#336791"
            strokeWidth="1.5"
          />
        </svg>
      ),
    },
    {
      name: 'dbt',
      category: 'Transform',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="#FF694B"
            fillOpacity="0.15"
            stroke="#FF694B"
            strokeWidth="1.5"
          />
          <path
            d="M8 12h8"
            stroke="#FF694B"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M12 8v8"
            stroke="#FF694B"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M9.5 9.5l5 5"
            stroke="#FF694B"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      name: 'Airflow',
      category: 'Orchestration',
      icon: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
          <path
            d="M12 2l3 5h-2v4h4v-2l5 3-5 3v-2h-4v4h2l-3 5-3-5h2v-4H7v2l-5-3 5-3v2h4V7h-2l3-5z"
            fill="#017CEE"
            fillOpacity="0.85"
          />
          <circle cx="12" cy="12" r="2" fill="#00C7D4" />
        </svg>
      ),
    },
  ];

  const pipelineSteps = [
    {
      number: 1,
      title: 'Collect',
      description:
        'We connect to your existing sources — spreadsheets, databases, files, and APIs.',
    },
    {
      number: 2,
      title: 'Upload',
      description:
        'Your data is securely migrated to the cloud with proper structure and backups.',
    },
    {
      number: 3,
      title: 'Transform',
      description:
        'We clean, combine, and reshape your data into consistent, reliable formats.',
    },
    {
      number: 4,
      title: 'Visualize',
      description:
        'Interactive dashboards give your team real-time visibility into key metrics.',
    },
  ];

  const benefits = [
    {
      stat: '100%',
      title: 'Cloud-Based',
      description:
        'Your data lives securely in the cloud — accessible from anywhere, backed up automatically, and ready to scale.',
    },
    {
      stat: '0',
      title: 'Manual Busywork',
      description:
        'Automated pipelines handle the repetitive tasks of moving and transforming data, so your team focuses on decisions.',
    },
    {
      stat: '24/7',
      title: 'Always Up to Date',
      description:
        'Dashboards refresh automatically with the latest data. No more emailing spreadsheets or waiting for reports.',
    },
    {
      stat: '1',
      title: 'Single Source of Truth',
      description:
        'All your data in one organized place — no more hunting across drives, folders, and inboxes for the right file.',
    },
  ];

  const deliverables = [
    'Cloud migration & setup',
    'File digitalization & organization',
    'Data pipeline development',
    'Dashboard design & deployment',
    'Team training & documentation',
    'Ongoing support',
  ];

  const faqs = [
    {
      question: 'What types of files and data can you work with?',
      answer:
        'Virtually anything — spreadsheets, PDFs, paper documents, databases, CSV files, and data from tools like Salesforce, HubSpot, or QuickBooks. If your business generates it, we can work with it.',
    },
    {
      question: 'Which cloud platform do you use?',
      answer:
        "We work with Google Cloud, AWS, and Azure. If you already have a platform, we'll use it. If not, we'll recommend the best fit for your needs and budget.",
    },
    {
      question: 'How long does a typical project take?',
      answer:
        'A basic cloud migration and dashboard setup can be ready in 2-3 weeks. Larger projects with multiple data sources and custom pipelines typically take 4-8 weeks. We deliver in phases so you see results early.',
    },
    {
      question: 'Do we need technical skills to use the dashboards?',
      answer:
        'Not at all. We build dashboards that are intuitive and easy to use. We also train your team and provide documentation so everyone feels confident.',
    },
    {
      question: 'Is our data secure?',
      answer:
        'Absolutely. We use encrypted connections, role-based access controls, and follow cloud security best practices. Your data stays private and protected.',
    },
    {
      question: 'What happens after everything is set up?',
      answer:
        'We offer ongoing support to keep things running smoothly — adding new data sources, updating dashboards, and optimizing pipelines as your business evolves.',
    },
  ];

  return (
    <>
      <PageWrapper>
        {/* Hero */}
        <HeroSection>
          <HeroGlowOrb />
          <HeroContainer>
            <HeroContent>
              <HeroTitle>Your Data, Organized and Working for You</HeroTitle>
              <HeroSubtitle>
                We move your data to the cloud, digitalize your files, build
                automated pipelines, and create dashboards that give your team
                real-time insights.
              </HeroSubtitle>
              <Button
                onClick={() => setIsContactOpen(true)}
                styles={`
                  padding: 18px 40px;
                  background: #06B6D4;
                  color: white;
                  font-weight: 700;
                  border-radius: 10px;
                  font-size: 18px;
                  transition: all 0.2s ease;
                  border: 2px solid #06B6D4;

                  &:hover {
                    background: transparent;
                    color: white;
                    border-color: white;
                  }
                `}
              >
                Get Started
              </Button>
            </HeroContent>
            <HeroAnimationWrapper>
              <LottieAnimation
                animationData={dataEngAnimation}
                width="100%"
                height="100%"
                loop
                autoplay
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid meet',
                  progressiveLoad: false,
                }}
              />
            </HeroAnimationWrapper>
          </HeroContainer>
        </HeroSection>

        {/* Services */}
        <ServicesSection>
          <SectionContainer>
            <SectionTitle>What We Do</SectionTitle>
            <SectionSubtitle>
              From messy spreadsheets to clean dashboards — we handle the full
              data journey.
            </SectionSubtitle>
            <ServicesGrid>
              {services.map((service) => (
                <ServiceCard key={service.title}>
                  <ServiceIcon>{service.icon}</ServiceIcon>
                  <ServiceTitle>{service.title}</ServiceTitle>
                  <ServiceDescription>{service.description}</ServiceDescription>
                </ServiceCard>
              ))}
            </ServicesGrid>
          </SectionContainer>
        </ServicesSection>

        {/* Tech Stack */}
        <TechStackSection>
          <SectionContainer>
            <SectionTitle $light>Tools We Work With</SectionTitle>
            <SectionSubtitle $light>
              Industry-leading platforms for cloud, analytics, and data
              transformation.
            </SectionSubtitle>
            <TechGrid>
              {techStack.map((tech) => (
                <TechCard key={tech.name}>
                  {tech.icon}
                  <TechName>{tech.name}</TechName>
                  <TechCategory>{tech.category}</TechCategory>
                </TechCard>
              ))}
            </TechGrid>
          </SectionContainer>
        </TechStackSection>

        {/* Architecture / Pipeline */}
        <ArchitectureSection>
          <SectionContainer>
            <SectionTitle>How It Works</SectionTitle>
            <SectionSubtitle>
              A straightforward process from raw data to actionable insights.
            </SectionSubtitle>
            <PipelineContainer>
              {pipelineSteps.map((step) => (
                <PipelineStep key={step.number}>
                  <PipelineStepNumber>{step.number}</PipelineStepNumber>
                  <PipelineStepTitle>{step.title}</PipelineStepTitle>
                  <PipelineStepDescription>
                    {step.description}
                  </PipelineStepDescription>
                </PipelineStep>
              ))}
            </PipelineContainer>
          </SectionContainer>
        </ArchitectureSection>

        {/* Benefits */}
        <BenefitsSection>
          <SectionContainer>
            <SectionTitle>Why Work With Us</SectionTitle>
            <BenefitsGrid>
              {benefits.map((benefit, index) => (
                <BenefitCard key={benefit.title} $index={index}>
                  <BenefitStat>{benefit.stat}</BenefitStat>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitDescription>{benefit.description}</BenefitDescription>
                </BenefitCard>
              ))}
            </BenefitsGrid>
          </SectionContainer>
        </BenefitsSection>

        {/* CTA */}
        <CTASection>
          <SectionContainer>
            <CTACard>
              <CTATitle>Ready to Get Your Data in Order?</CTATitle>
              <CTADescription>
                Tell us where you are today and we&apos;ll build a plan to get
                your data organized, accessible, and driving decisions.
              </CTADescription>
              <CTADeliverables>
                {deliverables.map((item) => (
                  <DeliverableItem key={item}>{item}</DeliverableItem>
                ))}
              </CTADeliverables>
              <CTAButton onClick={() => setIsContactOpen(true)}>
                Schedule a Free Consultation
              </CTAButton>
            </CTACard>
          </SectionContainer>
        </CTASection>

        {/* FAQ */}
        <FAQSection>
          <SectionContainer>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <FAQContainer>
              {faqs.map((faq) => (
                <FAQItem key={faq.question}>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                  <FAQAnswer>{faq.answer}</FAQAnswer>
                </FAQItem>
              ))}
            </FAQContainer>
          </SectionContainer>
        </FAQSection>
      </PageWrapper>
      <ContactForm
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
      />
    </>
  );
};

export default DataEngineering;
